import type { OnInit } from '@angular/core';
import { Component, computed, inject, input } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { WINDOW } from '@innogy/utils-dom';
import { DeployUrlPipe } from '@innogy/utils/environment';
import { getLogoHomepageNavigationLabel } from '@core/navigation-ui';

import type {
  FooterUsefulLinksNavigation,
  FooterWebNavigation,
} from '../footer/footer.models';
import { FooterComponent } from './../footer/footer.component';
import type { FooterRendering } from './footer-container.models';

@Component({
  selector: 'wl-footer-container',
  templateUrl: './footer-container.component.html',
  standalone: true,
  imports: [FooterComponent, DeployUrlPipe],
})
export class FooterContainerComponent implements OnInit {
  /** Input property for the footer rendering data from the CMS */
  readonly rendering = input.required<FooterRendering>();

  readonly #windowRef = inject(WINDOW);

  /** Brand configuration from environment */
  readonly #brand = inject(ENVIRONMENT_CONFIG).brand;

  /** Base path of application */
  readonly basePath = inject(ENVIRONMENT_CONFIG).basePath ?? '/';

  /** Brand name based on environment configuration */
  readonly brandName = this.#brand === 'ed' ? 'Energiedirect' : 'Essent';

  /** Current year for copyright information */
  readonly currentYear: number = new Date().getFullYear();

  /** Logo dimensions based on brand */
  readonly logoSizing =
    this.#brand === 'ed'
      ? { width: 220, height: 34 }
      : { width: 120, height: 40 };

  /** Aria-label for logo link */
  readonly logoLabel = getLogoHomepageNavigationLabel(
    inject(ENVIRONMENT_CONFIG)
  );

  /**
   * Formats web navigation data for footer menu structure
   * @returns Array of navigation categories with their respective items
   */
  readonly webNavigation = computed<FooterWebNavigation>(() => {
    const navigation = this.rendering().fields?.['Navigation'];

    if (!navigation) {
      return [];
    }

    return navigation.map((category) => ({
      title: category.fields.title.value ?? '<TITLE>',
      items: category.fields.items.map((item) => ({
        title: item.fields.name.value ?? '<SUB TITLE>',
        url: item.fields.link.value?.href ?? '/',
      })),
    }));
  });

  /**
   * Formats useful links data for the footer
   * @returns Array of useful links with titles and URLs
   */
  readonly usefulLinks = computed<FooterUsefulLinksNavigation>(() => {
    const usefulLinks = this.rendering().fields?.UsefulLinks;

    if (!usefulLinks) {
      return [];
    }

    return usefulLinks.map((item) => ({
      title: item.fields.name.value ?? '<TITLE>',
      url: item.fields.link.value?.href ?? '/',
    }));
  });

  /**
   * Lifecycle hook that validates the rendering data on component initialization
   * @throws Error if the footer data source is missing
   */
  ngOnInit() {
    if (!this.rendering().fields) {
      throw new Error(
        `The Footer on page "${this.#windowRef.location.pathname}" is missing a DataSource!`
      );
    }
  }
}
